import { useEffect, useState } from "react";
import { api, currency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [cfg, setCfg] = useState(null);
  const [calc, setCalc] = useState({
    unit_cost: 100, desired_margin: 0.4, risk_buffer: 0.05, expected_monthly_units: 25,
  });
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/pricing/config").then(r => setCfg(r.data));
  }, []);

  const runCalc = async () => {
    try {
      const r = await api.post("/pricing/calculate", calc);
      setResult(r.data);
    } catch { toast.error("Calc failed"); }
  };

  const saveCfg = async () => {
    try {
      await api.put("/pricing/config", cfg);
      toast.success("Pricing config saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2 flex items-center gap-1.5">
          <Calculator className="w-3 h-3" strokeWidth={1.5} /> Pricing
        </div>
        <h1 className="font-display text-4xl tracking-tight" data-testid="pricing-title">Pricing Engine</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Suggested price = Loaded unit cost / (1 − margin − risk). Loaded cost adds a fixed cost buffer.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border shadow-sm">
          <h3 className="font-display text-xl mb-4">Global drivers</h3>
          {cfg ? (
            <div className="grid grid-cols-2 gap-3">
              <F label="Upfront investment (R)">
                <Input type="number" data-testid="cfg-upfront" value={cfg.upfront_investment} onChange={(e) => setCfg({ ...cfg, upfront_investment: Number(e.target.value) })} />
              </F>
              <F label="Overhead recovery (0-1)">
                <Input type="number" step="0.01" data-testid="cfg-overhead" value={cfg.overhead_recovery} onChange={(e) => setCfg({ ...cfg, overhead_recovery: Number(e.target.value) })} />
              </F>
              <F label="Payback months">
                <Input type="number" data-testid="cfg-payback" value={cfg.payback_months} onChange={(e) => setCfg({ ...cfg, payback_months: Number(e.target.value) })} />
              </F>
              <F label="Rounding step (R)">
                <Input type="number" step="0.5" data-testid="cfg-rounding" value={cfg.rounding} onChange={(e) => setCfg({ ...cfg, rounding: Number(e.target.value) })} />
              </F>
            </div>
          ) : <div className="text-sm text-muted-foreground">Loading…</div>}
          <div className="mt-4 flex justify-end">
            <Button data-testid="save-cfg-btn" onClick={saveCfg} className="rounded-full bg-primary hover:bg-primary/90">Save drivers</Button>
          </div>
        </Card>

        <Card className="p-6 border-border shadow-sm">
          <h3 className="font-display text-xl mb-4">Live calculator</h3>
          <div className="grid grid-cols-2 gap-3">
            <F label="Unit cost (R)">
              <Input type="number" step="0.01" data-testid="calc-cost" value={calc.unit_cost} onChange={(e) => setCalc({ ...calc, unit_cost: Number(e.target.value) })} />
            </F>
            <F label="Desired margin (0-1)">
              <Input type="number" step="0.01" data-testid="calc-margin" value={calc.desired_margin} onChange={(e) => setCalc({ ...calc, desired_margin: Number(e.target.value) })} />
            </F>
            <F label="Risk buffer (0-1)">
              <Input type="number" step="0.01" data-testid="calc-risk" value={calc.risk_buffer} onChange={(e) => setCalc({ ...calc, risk_buffer: Number(e.target.value) })} />
            </F>
            <F label="Expected monthly units">
              <Input type="number" step="1" data-testid="calc-monthly" value={calc.expected_monthly_units} onChange={(e) => setCalc({ ...calc, expected_monthly_units: Number(e.target.value) })} />
            </F>
          </div>
          <div className="mt-4 flex justify-end">
            <Button data-testid="run-calc-btn" onClick={runCalc} className="rounded-full bg-primary hover:bg-primary/90">Calculate</Button>
          </div>

          {result && (
            <div className="mt-6 border-t pt-4 space-y-2 font-mono text-sm" data-testid="calc-result">
              <Row k="Expected volume">{result.expected_volume}</Row>
              <Row k="Fixed cost buffer">{currency(result.fixed_cost_buffer)}</Row>
              <Row k="Loaded unit cost">{currency(result.loaded_unit_cost)}</Row>
              <Row k="Raw price">{currency(result.raw_price)}</Row>
              <div className="mt-4 flex items-baseline justify-between">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Suggested price</div>
                <div className="font-display text-3xl text-primary" data-testid="calc-suggested">{currency(result.suggested_price)}</div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Row({ k, children }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span>{children}</span>
    </div>
  );
}
